import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import useUnauthorizedHandler from "../snippet/useUnauthorizedHandler";
import { debounce } from "../snippet/Debouncing";
import { useSelector } from "react-redux";
import { GetAllProviderListForReport } from "../../actions/admin-provider/reports/GetAllProviderListForReport";
import SelectAll from "./SelectAll";

function AllProviderMultiSelectDropdown({ title, updateFilter ,Path, client_org_ids }) {
  const AllFunctions = useSelector((state) => state.allFunctions);
  const [provider_org_ids, setProvider_org_ids] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [items, setItems] = useState([]);
  const [loadData, setLoadData] = useState(true);
  const [checkData, setCheckData] = useState(false);
  const dropdownRef = useRef(null);
  const handleUnauthorizedAccess = useUnauthorizedHandler();
  const dispatch = useDispatch();

  const normalizedClientIdsStr = JSON.stringify(
    (client_org_ids || [])
      .map((item) => (item && typeof item === "object" ? item?.organisation_id : item))
      .map(Number)
      .filter(Boolean)
      .sort((a, b) => a - b)
  );

  const prevClientIdsRef = useRef(normalizedClientIdsStr);
  const isFirstRender = useRef(true);

  const visibleProviderIds = (items || [])
    .map((item) => parseInt(item.provider_org_id, 10))
    .filter(Boolean);

  const selectedVisibleCount = visibleProviderIds.filter((id) =>
    provider_org_ids.some((entry) => Number(entry?.provider_org_id) === Number(id))
  ).length;

  const allVisibleSelected =
    visibleProviderIds.length > 0 &&
    selectedVisibleCount === visibleProviderIds.length;
  const someVisibleSelected =
    selectedVisibleCount > 0 && selectedVisibleCount < visibleProviderIds.length;

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const handleSeachChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const inputchangeSearch = useCallback(debounce(handleSeachChange), []);
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (item) => {
    setCheckData(true);
    const newEntry = {
      f_a_relation_id: item.f_a_relation_id,
      provider_org_id: parseInt(item.provider_org_id, 10),
    };

    const exists = provider_org_ids.some(
      (entry) => Number(entry.provider_org_id) === Number(newEntry.provider_org_id)
    );

    if (exists) {
      setProvider_org_ids(
        provider_org_ids.filter(
          (entry) => Number(entry.provider_org_id) !== Number(newEntry.provider_org_id)
        )
      );
    } else {
      setProvider_org_ids([...provider_org_ids, newEntry]);
    }
  };

  const clearSelection = () => {
    setCheckData(true);
    setProvider_org_ids([]);
  };

  const toggleSelectAll = () => {
    setCheckData(true);

    setProvider_org_ids((prev) => {
      const prevMap = new Map(
        (prev || []).map((e) => [Number(e?.provider_org_id), e])
      );

      const currentlyAllSelected =
        visibleProviderIds.length > 0 &&
        visibleProviderIds.every((id) => prevMap.has(Number(id)));

      if (currentlyAllSelected) {
        visibleProviderIds.forEach((id) => prevMap.delete(Number(id)));
      } else {
        (items || []).forEach((item) => {
          const id = parseInt(item.provider_org_id, 10);
          if (!id) return;
          if (visibleProviderIds.includes(id)) {
            prevMap.set(id, {
              f_a_relation_id: item.f_a_relation_id,
              provider_org_id: id,
            });
          }
        });
      }

      return Array.from(prevMap.values());
    });
  };

  useEffect(() => {
    if (checkData) {
      const allProvidersSelected =
        !searchTerm?.trim() &&
        visibleProviderIds.length > 1 &&
        visibleProviderIds.every((id) =>
          provider_org_ids.some((entry) => Number(entry?.provider_org_id) === Number(id))
        );

      updateFilter("provider_org_ids", allProvidersSelected ? [] : provider_org_ids);
    }
  }, [provider_org_ids]);

  const GetClientIdData = useCallback(async () => {
    const apiParams = { search: searchTerm };

    const parsedClientIds = JSON.parse(normalizedClientIdsStr || "[]");
    if (parsedClientIds.length > 0) {
      apiParams.client_org_ids = JSON.stringify(parsedClientIds);
    }

    dispatch(GetAllProviderListForReport(apiParams, Path)).then(
      (response) => {
        if (response?.success === true) {
          const arrayData = response?.data;
          // Deduplicate by provider_org_id to prevent 35->34 bug
          const uniqueItemsMap = new Map();
          arrayData?.forEach((item) => {
            const provider_org_id = item.org_data?.organisation_id;
            if (provider_org_id && !uniqueItemsMap.has(provider_org_id)) {
              uniqueItemsMap.set(provider_org_id, {
                provider_trading_name: item.org_data?.trading_name,
                provider_org_id,
                f_a_relation_id: item.function_assignment_id,
              });
            }
          });
          setItems(Array.from(uniqueItemsMap.values()));
        } else if (response?.status === 401) {
          handleUnauthorizedAccess();
        } else {
          setLoadData(true);
        }
      }
    );
  }, [dispatch, searchTerm, normalizedClientIdsStr, Path, handleUnauthorizedAccess]);

  useEffect(() => {
    GetClientIdData();
  }, [searchTerm, normalizedClientIdsStr, GetClientIdData]);

  // Client filter precedence: clear provider selection ONLY when client selection actually changes
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevClientIdsRef.current = normalizedClientIdsStr;
      return;
    }

    if (prevClientIdsRef.current !== normalizedClientIdsStr) {
      prevClientIdsRef.current = normalizedClientIdsStr;
      setProvider_org_ids([]);
      setCheckData(true);
      updateFilter("provider_org_ids", []);
    }
  }, [normalizedClientIdsStr, updateFilter]);

  return (
    <div className="relative inline-block text-left w-32" ref={dropdownRef}>
      {/* Dropdown button */}
      <button
        onClick={toggleDropdown}
        type="button"
        className="flex items-center justify-between w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
      >
        <span>{title}</span>
        {provider_org_ids?.length > 0 && (
          <span className="inline-flex ml-1 items-center justify-center w-6 h-6 text-sm font-semibold text-blue-700 bg-blue-100 rounded-full">
            {provider_org_ids.length}
          </span>
        )}
        <svg
          className="w-5 h-5 ml-2 text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute left-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-10">
          {/* Header row: 'X provider_org_ids' and Clear button */}
          <div className="flex items-center justify-between p-2 border-b border-gray-200">
            <span className="text-sm text-gray-500">
              {provider_org_ids.length} selected
            </span>
            <button
              type="button"
              className="text-sm text-blue-600 hover:underline focus:outline-none"
              onClick={clearSelection}
            >
              Clear
            </button>
          </div>

          {/* Search input */}
          <div className="p-2">
            <input
              type="text"
              placeholder="Type here"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              // value={searchTerm}
              onChange={(e) => inputchangeSearch(e)}
            />
          </div>

          <SelectAll
            checked={allVisibleSelected}
            indeterminate={someVisibleSelected}
            disabled={visibleProviderIds.length === 0}
            onChange={toggleSelectAll}
          />

          {/* Scrollable list of items */}
          <ul className="max-h-48 overflow-y-auto">
            {items?.map((item) => (
              <li key={item.provider_org_id}>
                <label className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer w-full">
                  <input
                    type="checkbox"
                    className="w-4 h-4 mr-2 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                    checked={provider_org_ids?.some(
                      (filterItem) =>
                        Number(filterItem.provider_org_id) ===
                        Number(item.provider_org_id)
                    )}
                    onChange={() => handleSelect(item)}
                  />
                  <span className="text-sm text-gray-700 select-none">
                    {item.provider_trading_name}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default AllProviderMultiSelectDropdown;
